// File: src/components/season/SeasonDetailPage.js
import React, { useState, useEffect, useRef } from "react";
import { Link, } from "react-router-dom";
import axios from "axios";
import AddTeamModal from "../teams/AddTeamModal";
import AddLocationModal from "./AddLocationModal";
import AddGameModal from "../games/AddGameModal";
import useUserRoles from '../../hooks/useUserRoles';
import useDebounce from '../utils/debouncUtil';
import truncateString from '../utils/truncateUtil';

// Utility function to sort events by datetime
const sortEventsByDate = (eventsArray) => {
	return eventsArray.sort((a, b) => {
		const dateA = new Date(a.datetime);
		const dateB = new Date(b.datetime);
		return dateA - dateB;
	});
};

function ProfileSeasonDetailPage({ activeLeague, setActiveLeague, activeSeason, setActiveSeason }) {
	const { isCommissioner } = useUserRoles();
	// Use URL param or prop for seasonId
	// const { seasonId: paramSeasonId } = useParams();
	//const [searchParams] = useSearchParams(); // Get query parameters

	// const seasonId = propSeasonId || paramSeasonId;
	const seasonId = activeSeason.id;
	const leagueId = activeLeague.id;

	// State for API data, inner tab control, etc.
	const [teams, setTeams] = useState([]);
	const [games, setGames] = useState([]);
	const [events, setEvents] = useState([]);
	const [seasonDetails, setSeasonDetails] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	// const [isChecked, setIsChecked] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);

	const [showLocationModal, setShowLocationModal] = useState(false);

	const [showGameModal, setShowGameModal] = useState(false);

	// Controlled state for inner tab navigation; defaults to "Teams"
	const [innerActiveTab, setInnerActiveTab] = useState("Teams");

	// Search and pagination state for Players tab
	const [searchTerm, setSearchTerm] = useState('');
	const [filteredPlayers, setFilteredPlayers] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const searchTimeoutRef = useRef(null);
	const [gameTypeFilter, setGameTypeFilter] = useState('all');
	// Debounced search term
	const debouncedSearchTerm = useDebounce(searchTerm, 300);

	// Get the is_played value from query params or default to false
	//const isPlayed = searchParams.get('is_played') === 'true';

	const filteredGames = games.filter(game => {
		if (gameTypeFilter === 'all') return true;
		return game.game_type === gameTypeFilter;
	});

	useEffect(() => {
		// Fetch games for the league
		//`/api/games/seasons/${seasonId}/`
		axios
			.get(`/api/games/leagues/${leagueId}/`, {
				params: { season_id: seasonId }, // Pass is_played dynamically
				headers: {
					Authorization: `Bearer ${localStorage.getItem('access_token')}`,
				},
			})
			.then((response) => setGames(response.data))
			.catch((error) => {
				console.error('Error fetching games:', error);
				alert('Unable to fetch games.');
			});
	}, [seasonId, leagueId]);

	useEffect(() => {
		// Fetch events for the season
		axios
			.get(`/api/leagues/events/?season_id=${seasonId}`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem('access_token')}`,
				},
			})
			.then((response) => {
				// Sort events by datetime (nearest to furthest)
				const sortedEvents = sortEventsByDate(response.data);
				setEvents(sortedEvents);
			})
			.catch((error) => {
				// Error fetching events - don't show alert as they might not exist yet
			});
	}, [seasonId]);

	useEffect(() => {
		axios
			.get(`/api/leagues/seasons/${seasonId}/`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("access_token")}`,
				},
			})
			.then((response) => {
				setSeasonDetails(response.data);
				setLoading(false);
			})
			.catch((error) => {
				console.error("Error fetching season details:", error);
				setError("Failed to load season details.");
				setLoading(false);
			});

		axios
			.get(`/api/teams/${seasonId}/teams/`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("access_token")}`,
				},
			})
			.then((response) => {
				setTeams(response.data);
			})
			.catch((error) => {
				console.error("Error fetching teams:", error);
				alert("Unable to fetch teams.");
			});
	}, [seasonId]);

	// Filter and paginate players based on search term
	useEffect(() => {
		if (!seasonDetails?.participants) return;

		const filtered = seasonDetails.participants.filter(player => {
			const fullName = `${player.first_name} ${player.last_name}`.toLowerCase();
			const email = (player.email || '').toLowerCase();
			const phone = (player.phone_number || '').toLowerCase();
			const searchLower = debouncedSearchTerm.toLowerCase();

			return fullName.includes(searchLower) ||
				email.includes(searchLower) ||
				phone.includes(searchLower);
		});

		setFilteredPlayers(filtered);
		setTotalCount(filtered.length);
		setTotalPages(Math.ceil(filtered.length / pageSize));
		setCurrentPage(1);
	}, [debouncedSearchTerm, seasonDetails?.participants, pageSize]);

	// Get current page players
	const getCurrentPagePlayers = () => {
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return filteredPlayers.slice(startIndex, endIndex);
	};

	const handleSearch = (e) => {
		const value = e.target.value;
		setSearchTerm(value);
		setCurrentPage(1);

		// Clear any existing timeout
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		// Set new timeout for immediate visual feedback
		searchTimeoutRef.current = setTimeout(() => {
			// This is handled by the debounced search term
		}, 300);
	};

	const handlePageSizeChange = (e) => {
		setPageSize(Number(e.target.value));
		setCurrentPage(1);
	};

	const handlePageChange = (newPage) => {
		setCurrentPage(newPage);
	};

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, []);

	if (loading) {
		return <div className="container">Loading season details...</div>;
	}
	if (error) {
		return <div className="container text-danger">{error}</div>;
	}

	const handleShow = () => setModalVisible(true);
	const handleClose = () => setModalVisible(false);
	// const handleCheckboxChange = () => setIsChecked(!isChecked);

	const handleShowLocationModal = () => setShowLocationModal(true);
	const handleCloseLocationModal = () => setShowLocationModal(false);

	const handleShowGameModal = () => setShowGameModal(true);
	const handleCloseGameModal = () => setShowGameModal(false);

	const handleLocationAdded = (newLocation) => {
		setSeasonDetails((prev) => ({
			...prev,
			locations: prev.locations ? [...prev.locations, newLocation] : [newLocation],
		}));
	};

	const handleTeamAdded = (newTeam) => {
		setTeams((prevTeams) => [...prevTeams, newTeam]);
	};

	const handleGameAdded = (newGame) => {
		setGames((prevGames) => [...prevGames, newGame]);
	};

	const renderCreateButton = () => {
		if (!isCommissioner) {
			// non‑commissioners get nothing
			return null;
		}
		switch (innerActiveTab) {
			case "Teams":
				return (
					<button className="btn btn-primary-soft" onClick={handleShow}>
						<i className="fa-solid fa-plus pe-1"></i> Create Team
					</button>
				);
			case "Schedule":
				return (
					<button
						className="btn btn-primary-soft"
						onClick={handleShowGameModal}
					>
						<i className="fa-solid fa-plus pe-1"></i> Create Schedule
					</button>
				);
			case "Events":
				return (
					<button
						className="btn btn-primary-soft"
						data-bs-toggle="modal"
						data-bs-target="#modalCreateEvents"
					>
						<i className="fa-solid fa-plus pe-1"></i> Create Event
					</button>
				);
			default:
				return null;
		}
	};

	const renderLeagueButton = () => {
		if (!isCommissioner) {
			return <span className="mb-0">{seasonDetails.league_name}</span>;
		}
		return (
			<Link
				className="btn btn-link text-decoration-none p-0"
				to="/league-manager/"
			>
				{seasonDetails.league_name}
			</Link>
		);
	};


	return (
		// Render this page as the Leagues tab pane in your SettingsLayout (id="nav-setting-tab-6")
		<div className="tab-pane fade show active" id="nav-setting-tab-6">
			<div className="container">

				{/* Season Detail Header */}
				<div className="container ps-0 pe-0">
					<div className="card mb-3 p-0">
						<div
							className="h-200px rounded-top"
							style={{
								backgroundImage: `url(${seasonDetails.banner ||
									`/static/images/kb_static/lemont_rec_league_banner.png`
									})`,
								backgroundPosition: "center",
								backgroundSize: "cover",
								backgroundRepeat: "no-repeat",
							}}
						></div>
					</div>
				</div>

				{/* Season Details */}
				<div className="card card-body mb-3">
					<div className="row g-4">
						<div className="col-6 col-lg-4">
							<h5>Start Date</h5>
							<p className="small mb-0">{seasonDetails.start_date}</p>
						</div>
						<div className="col-6 col-lg-4">
							<h5>End Date</h5>
							<p className="small mb-0">{seasonDetails.end_date}</p>
						</div>
						<div className="col-sm-6 col-lg-4">
							<h5>League</h5>
							<p className="small mb-0">
								{renderLeagueButton()}
							</p>
						</div>
						<div className="col-sm-6 col-lg-4">
							<h5>Fields</h5>
							{seasonDetails.locations && seasonDetails.locations.length > 0 ? (
								<ul className="list-unstyled">
									{seasonDetails.locations.map((location) => (
										<li key={location.id}>
											{location.name}, {location.city}
										</li>
									))}
								</ul>
							) : (
								<p className="small mb-0">No locations available</p>
							)}
							{isCommissioner && (
								<button className="btn btn-primary mt-2" onClick={handleShowLocationModal}>
									Add Location
								</button>
							)}
						</div>
					</div>
				</div>

				{/* Inner Tab Navigation */}
				<div className="card h-100">
					<div className="card-header d-sm-flex align-items-center text-center justify-content-sm-between border-0 pb-0">
						<h1 className="h4 card-title">Season Details</h1>
						{renderCreateButton()}
					</div>

					<div className="card-body">
						<ul className="nav nav-tabs nav-bottom-line justify-content-center justify-content-md-start">
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Teams" ? "active" : ""}`}
									href="#tab-1"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Teams");
									}}
								>
									Teams
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Schedule" ? "active" : ""}`}
									href="#tab-2"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Schedule");
									}}
								>
									Schedule
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Players" ? "active" : ""}`}
									href="#tab-3"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Players");
									}}
								>
									Players
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Events" ? "active" : ""}`}
									href="#tab-4"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Events");
									}}
								>
									Events
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Standings" ? "active" : ""}`}
									href="#tab-5"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Standings");
									}}
								>
									Standings
								</a>
							</li>
							{/* <li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Rules" ? "active" : ""}`}
									href="#tab-6"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Rules");
									}}
								>
									Rules
								</a>
							</li> */}
						</ul>

						{/* Inner Tab Content */}
						<div className="tab-content mb-0 pb-0">
							<div
								className={`tab-pane fade ${innerActiveTab === "Teams" ? "show active" : ""}`}
								id="tab-1"
							>
								<div className="container">
									<div className="row g-4">
										{teams.length > 0 ? (
											teams.map((team) => (
												<div key={team.id} className="col-sm-6 col-xl-4">
													<Link to={`/teams/${team.id}`} className="text-decoration-none">
														<div className="card h-100">
															<div className="position-relative">
																<img
																	className="img-fluid rounded-top w-100"
																	src={
																		team.logo ||
																		`/static/images/kb_static/lemont_rec_leagues_circle.png`
																	}
																	alt={`${team.name} logo`}
																	style={{
																		height: "280px",
																		objectFit: "cover",
																	}}
																/>
															</div>
															<div className="card-body position-relative pt-0">
																<h6 className="mt-2 text-dark">{team.name}</h6>
																<p className="mb-1 small text-muted">
																	<i className="bi bi-trophy pe-1"></i> Wins: {team.wins}
																</p>
																<p className="mb-1 small text-muted">
																	<i className="bi bi-trophy-fill pe-1"></i> Losses: {team.losses}
																</p>
																<p className="mb-0 small text-muted">
																	<i className="bi bi-trophy-fill pe-1"></i> Draws: {team.draws}
																</p>
															</div>
														</div>
													</Link>
												</div>
											))
										) : (
											<div className="col-12">
												<p>No teams found.</p>
											</div>
										)}
									</div>
								</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Schedule" ? "show active" : ""}`}
								id="tab-2"
							>
								<div className="container">
									{/* Filter Controls */}
									<div className="row mb-3">
										<div className="col-md-6">
											<label htmlFor="gameTypeFilter" className="form-label">Filter by Game Type:</label>
											<select
												id="gameTypeFilter"
												className="form-select"
												value={gameTypeFilter}
												onChange={(e) => setGameTypeFilter(e.target.value)}
											>
												<option value="all">All Games</option>
												<option value="preseason">Preseason</option>
												<option value="regular">Regular Season</option>
												<option value="makeup">Makeup</option>
												<option value="playoff">Playoff</option>
												<option value="bye">Bye</option>
											</select>
										</div>
										<div className="col-md-6 d-flex align-items-end">
											<small className="text-muted">
												Showing {filteredGames.length} of {games.length} games
											</small>
										</div>
									</div>

									{/* Game Type Legend */}
									<div className="row mb-3">
										<div className="col-12">
											<div className="d-flex flex-wrap gap-2 align-items-center">
												<small className="text-muted me-2">Game Types:</small>
												<span className="badge bg-info">Preseason</span>
												<span className="badge bg-primary">Regular</span>
												<span className="badge bg-warning">Makeup</span>
												<span className="badge bg-success">Playoff</span>
												<span className="badge bg-secondary">Bye</span>
											</div>
										</div>
									</div>

									<div className="row g-4">
										{filteredGames.map((game) => {
											const homeParticipant =
												game.teams?.find((p) => p.is_home) || {};
											const awayParticipant =
												game.teams?.find((p) => p.is_away) || {};
											const isByeGame = game.game_type === 'bye';

											return (
												<div key={game.id} className="col-lg-4 col-md-6 col-sm-12">
													<div
														className="card h-100 shadow-sm position-relative"
														style={{
															transition: 'all 0.3s ease',
															cursor: 'pointer'
														}}
														onMouseEnter={(e) => {
															e.currentTarget.style.transform = 'translateY(-5px)';
															e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
														}}
														onMouseLeave={(e) => {
															e.currentTarget.style.transform = 'translateY(0)';
															e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
														}}
													>
														{/* Clickable area for game details */}
														<Link to={`/games/${game.id}/`} className="text-decoration-none">
															<div className="position-relative">
																{/* Game Type Badge - positioned above the image */}
																<div className="position-absolute top-0 start-0 m-2" style={{ zIndex: 10 }}>
																	<span className={`badge ${game.game_type === 'preseason' ? 'bg-info' :
																		game.game_type === 'regular' ? 'bg-primary' :
																			game.game_type === 'makeup' ? 'bg-warning' :
																				game.game_type === 'playoff' ? 'bg-success' : 'bg-secondary'
																		}`}>
																		{game.game_type ? game.game_type.charAt(0).toUpperCase() + game.game_type.slice(1) : 'Unknown'}
																	</span>
																</div>
																<div
																	style={{
																		width: '100%',
																		height: '200px',
																		position: 'relative',
																		overflow: 'hidden',
																		borderRadius: '8px 8px 0 0',
																	}}
																>
																	{isByeGame ? (
																		// For bye games, show only home team logo
																		homeParticipant.participant_logo ? (
																			<img
																				src={homeParticipant.participant_logo}
																				alt={`${homeParticipant.participant_name} logo`}
																				style={{
																					width: '100%',
																					height: '200px',
																					objectFit: 'cover',
																				}}
																			/>
																		) : (
																			// Placeholder for bye games
																			<div
																				style={{
																					width: '100%',
																					height: '200px',
																					backgroundColor: '#f8f9fa',
																					display: 'flex',
																					alignItems: 'center',
																					justifyContent: 'center',
																				}}
																			>
																				<i className="bi bi-people-fill" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
																			</div>
																		)
																	) : (
																		// For regular games, show split image
																		<>
																			{homeParticipant.participant_logo ? (
																				// Top half - Home Team logo
																				<img
																					src={homeParticipant.participant_logo}
																					alt={`${homeParticipant.participant_name} logo`}
																					style={{
																						width: '100%',
																						height: '200px',
																						objectFit: 'cover',
																						position: 'absolute',
																						left: 0,
																						top: 0,
																						clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
																					}}
																				/>
																			) : (
																				// Placeholder for top half - Blue
																				<div
																					style={{
																						width: '100%',
																						height: '50%',
																						backgroundColor: 'blue',
																						position: 'absolute',
																						top: 0,
																					}}
																				></div>
																			)}

																			{awayParticipant.participant_logo ? (
																				// Bottom half - Away Team logo
																				<img
																					src={awayParticipant.participant_logo}
																					alt={`${awayParticipant.participant_name} logo`}
																					style={{
																						width: '100%',
																						height: '200px',
																						objectFit: 'cover',
																						position: 'absolute',
																						left: 0,
																						bottom: 0,
																						clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
																					}}
																				/>
																			) : (
																				// Placeholder for bottom half - Red
																				<div
																					style={{
																						width: '100%',
																						height: '50%',
																						backgroundColor: 'red',
																						position: 'absolute',
																						bottom: 0,
																					}}
																				></div>
																			)}
																		</>
																	)}
																</div>
																<div className={`badge ${game.is_played ? 'bg-success' : 'bg-danger'} text-white mt-2 me-2 position-absolute top-0 end-0`}>
																	{game.is_played ? 'Played' : 'Upcoming'}
																</div>
															</div>
															<div className="card-body p-3">
																{isByeGame ? (
																	// Simplified display for bye games
																	<div className="text-center mb-2">
																		<h6 className="mb-1 text-dark fw-bold">
																			{homeParticipant.participant_name || 'Team'}
																		</h6>
																		<div className="text-muted small mb-1">(BYE)</div>
																	</div>
																) : (
																	// Regular game display
																	<div className="text-center mb-2">
																		<h6 className="mb-1 text-dark fw-bold">
																			{homeParticipant.participant_name || 'Home Team'}
																		</h6>
																		<div className="text-muted small mb-1">vs</div>
																		<h6 className="mb-0 text-dark fw-bold">
																			{awayParticipant.participant_name || 'Away Team'}
																		</h6>
																	</div>
																)}
																<hr className="my-2" />
																{!isByeGame && (
																	<div className="small text-muted">
																		<div className="mb-1">
																			<i className="bi bi-calendar-check me-2"></i>
																			{game.scheduled_date_only}
																		</div>
																		<div className="mb-1">
																			<i className="bi bi-clock me-2"></i>
																			{game.scheduled_time_only}
																		</div>
																		<div>
																			<i className="bi bi-geo-alt me-2"></i>
																			{game.location?.name || 'TBD'}
																		</div>
																	</div>
																)}
																{/* Add empty space for bye games to maintain card height */}
																{isByeGame && (
																	<div style={{ height: '60px' }}></div>
																)}
															</div>
														</Link>
													</div>
												</div>
											);
										})}
									</div>
								</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Players" ? "show active" : ""}`}
								id="tab-3"
							>
								<div className="container">
									{/* Search and Controls */}
									<div className="row mb-4">
										<div className="col-md-6">
											<div className="input-group">
												<span className="input-group-text">
													<i className="bi bi-search"></i>
												</span>
												<input
													type="text"
													className="form-control"
													placeholder="Search players..."
													value={searchTerm}
													onChange={handleSearch}
													autoComplete="off"
												/>
											</div>
										</div>
										<div className="col-md-3">
											<select
												className="form-select"
												value={pageSize}
												onChange={handlePageSizeChange}
											>
												<option value={5}>5 per page</option>
												<option value={10}>10 per page</option>
												<option value={20}>20 per page</option>
												<option value={30}>30 per page</option>
											</select>
										</div>
										<div className="col-md-3">
											<div className="text-end">
												<small className="text-muted">
													Showing {totalCount} players
												</small>
											</div>
										</div>
									</div>

									{/* Players Grid */}
									<div className="row g-4">
										{getCurrentPagePlayers().map((player) => (
											<div key={player.id} className="col-12 col-sm-6">
												<div className="card h-100">
													<div className="card-body">
														<div className="d-flex flex-column">
															<div className="d-flex align-items-center mb-3">
																<img
																	src={player.profile_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
																	alt={`${player.first_name} ${player.last_name}`}
																	className="rounded-circle me-3"
																	style={{ width: '60px', height: '60px', objectFit: 'cover' }}
																/>
																<div className="flex-grow-1">
																	<h6 className="mb-1">
																		{truncateString(player.first_name + ' ' + player.last_name, 20)}
																	</h6>
																	<small className="text-muted">
																		{player.team ? `Team: ${player.team}` : 'Free Agent'}
																	</small>
																</div>
															</div>
															<div className="mt-auto">
																{player.email && (
																	<div className="mb-2">
																		<small className="text-muted">
																			<i className="bi bi-envelope me-1"></i>
																			<a href={`mailto:${player.email}`} className="text-decoration-none">
																				{truncateString(player.email, 35)}
																			</a>
																		</small>
																	</div>
																)}
																{player.phone_number && (
																	<div className="mb-2">
																		<small className="text-muted">
																			<i className="bi bi-telephone me-1"></i>
																			<a href={`tel:${player.phone_number}`} className="text-decoration-none">
																				{truncateString(player.phone_number, 15)}
																			</a>
																		</small>
																	</div>
																)}
															</div>
														</div>
													</div>
												</div>
											</div>
										))}
										{getCurrentPagePlayers().length === 0 && (
											<div className="col-12">
												<div className="text-center text-muted py-4">
													{searchTerm ? 'No players found matching your search' : 'No players found'}
												</div>
											</div>
										)}
									</div>

									{/* Mobile-Friendly Pagination */}
									{totalPages > 1 && (
										<div className="d-flex flex-column align-items-center mt-4">
											<div className="text-center mb-3">
												<span className="text-muted">
													Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} players
												</span>
											</div>
											<nav aria-label="Page navigation">
												<ul className="pagination pagination-sm mb-0">
													<li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
														<button
															className="page-link"
															onClick={() => handlePageChange(currentPage - 1)}
															disabled={currentPage === 1}
														>
															<i className="bi bi-chevron-left"></i>
														</button>
													</li>

													{/* Show page numbers with ellipsis for mobile */}
													{totalPages <= 5 ? (
														// Show all pages if 5 or fewer
														[...Array(totalPages)].map((_, index) => {
															const pageNum = index + 1;
															return (
																<li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
																	<button
																		className="page-link"
																		onClick={() => handlePageChange(pageNum)}
																	>
																		{pageNum}
																	</button>
																</li>
															);
														})
													) : (
														// Show smart pagination for mobile
														<>
															{/* First page */}
															<li className={`page-item ${currentPage === 1 ? 'active' : ''}`}>
																<button
																	className="page-link"
																	onClick={() => handlePageChange(1)}
																>
																	1
																</button>
															</li>

															{/* Ellipsis if needed */}
															{currentPage > 3 && (
																<li className="page-item disabled">
																	<span className="page-link">...</span>
																</li>
															)}

															{/* Current page and neighbors */}
															{currentPage > 2 && currentPage < totalPages && (
																<li className="page-item">
																	<button
																		className="page-link"
																		onClick={() => handlePageChange(currentPage - 1)}
																	>
																		{currentPage - 1}
																	</button>
																</li>
															)}

															{currentPage !== 1 && currentPage !== totalPages && (
																<li className="page-item active">
																	<button className="page-link">
																		{currentPage}
																	</button>
																</li>
															)}

															{currentPage < totalPages - 1 && currentPage > 1 && (
																<li className="page-item">
																	<button
																		className="page-link"
																		onClick={() => handlePageChange(currentPage + 1)}
																	>
																		{currentPage + 1}
																	</button>
																</li>
															)}

															{/* Ellipsis if needed */}
															{currentPage < totalPages - 2 && (
																<li className="page-item disabled">
																	<span className="page-link">...</span>
																</li>
															)}

															{/* Last page */}
															<li className={`page-item ${currentPage === totalPages ? 'active' : ''}`}>
																<button
																	className="page-link"
																	onClick={() => handlePageChange(totalPages)}
																>
																	{totalPages}
																</button>
															</li>
														</>
													)}

													<li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
														<button
															className="page-link"
															onClick={() => handlePageChange(currentPage + 1)}
															disabled={currentPage === totalPages}
														>
															<i className="bi bi-chevron-right"></i>
														</button>
													</li>
												</ul>
											</nav>
										</div>
									)}
								</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Events" ? "show active" : ""}`}
								id="tab-4"
							>
								<div className="container">
									<div className="row g-4">
										{events.length > 0 ? (
											events.map((event) => (
												<div key={event.id} className="col-lg-4 col-md-6 col-sm-12">
													<div className="card h-100 shadow-sm">
														<div className="card-body">
															<div className="d-flex justify-content-between align-items-start mb-2">
																<h6 className="card-title mb-0">{event.title}</h6>
																{!event.is_public && (
																	<span className="badge bg-secondary">Private</span>
																)}
															</div>
															<div className="small text-muted">
																<div className="mb-1">
																	<i className="bi bi-calendar-event me-2"></i>
																	{event.formatted_datetime}
																</div>
																<div className="mb-1">
																	<i className="bi bi-geo-alt me-2"></i>
																	{event.location_name && (
																		<span className="fw-semibold">{event.location_name}</span>
																	)}
																	{event.location_name && event.address_line1 && <br />}
																	{event.address_line1 && (
																		<span>{event.address_line1}, {event.city}</span>
																	)}
																</div>
																{event.team_name && (
																	<div className="mb-1">
																		<i className="bi bi-people me-2"></i>
																		{event.team_name}
																	</div>
																)}
																{event.sponsor && (
																	<div className="mb-1 d-flex align-items-center">
																		{/* Sponsor Logo - Small and responsive */}
																		{event.sponsor.logo_url ? (
																			<div
																				className="me-2"
																				style={{
																					width: '24px',
																					height: '24px',
																					minWidth: '24px',
																					position: 'relative',
																					overflow: 'hidden',
																					borderRadius: '3px',
																					backgroundColor: '#f8f9fa',
																					border: '1px solid #dee2e6',
																				}}
																			>
																				<img
																					src={event.sponsor.logo_url}
																					alt={`${event.sponsor.name} logo`}
																					style={{
																						width: '100%',
																						height: '100%',
																						objectFit: 'contain',
																						padding: '1px',
																					}}
																					onError={(e) => {
																						// Hide the image and show the fallback icon if the image fails to load
																						e.target.style.display = 'none';
																						e.target.nextSibling.style.display = 'flex';
																					}}
																				/>
																				<div
																					className="d-flex align-items-center justify-content-center h-100"
																					style={{
																						display: 'none',
																						fontSize: '10px',
																						color: '#6c757d'
																					}}
																				>
																					<i className="bi bi-award"></i>
																				</div>
																			</div>
																		) : (
																			<i className="bi bi-award me-2"></i>
																		)}
																		Sponsored by {event.sponsor.name}
																	</div>
																)}
															</div>
														</div>
													</div>
												</div>
											))
										) : (
											<div className="col-12">
												<div className="text-center py-5">
													<i className="bi bi-calendar-event display-1 text-muted"></i>
													<h5 className="mt-3 text-muted">No Events Yet</h5>
													<p className="text-muted">No events have been created for this season.</p>
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Standings" ? "show active" : ""}`}
								id="tab-5"
							>
								<div className="container">
									{teams.length > 0 ? (
										<div className="table-responsive">
											<table className="table table-hover">
												<thead className="table-light">
													<tr>
														<th scope="col" className="text-center">Rank</th>
														<th scope="col">Team</th>
														<th scope="col" className="text-center">W</th>
														<th scope="col" className="text-center">L</th>
														<th scope="col" className="text-center">D</th>
														<th scope="col" className="text-center">Win %</th>
													</tr>
												</thead>
												<tbody>
													{teams
														.sort((a, b) => {
															// Primary sort: wins (descending)
															if (b.wins !== a.wins) {
																return b.wins - a.wins;
															}
															// Secondary sort: win percentage (descending)
															const aTotalGames = a.wins + a.losses + a.draws;
															const bTotalGames = b.wins + b.losses + b.draws;
															const aWinPercentage = aTotalGames > 0 ? a.wins / aTotalGames : 0;
															const bWinPercentage = bTotalGames > 0 ? b.wins / bTotalGames : 0;
															return bWinPercentage - aWinPercentage;
														})
														.map((team, index) => {
															const totalGames = team.wins + team.losses + team.draws;
															const winPercentage = totalGames > 0
																? ((team.wins / totalGames) * 100).toFixed(1)
																: '0.0';

															return (
																<tr key={team.id}>
																	<td className="text-center fw-bold">
																		{index + 1}
																	</td>
																	<td>
																		<div className="d-flex align-items-center">
																			<img
																				src={team.logo || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
																				alt={`${team.name} logo`}
																				className="rounded-circle me-3"
																				style={{ width: '40px', height: '40px', objectFit: 'cover' }}
																			/>
																			<span className="fw-semibold">{team.name}</span>
																		</div>
																	</td>
																	<td className="text-center text-success fw-bold">{team.wins}</td>
																	<td className="text-center text-danger">{team.losses}</td>
																	<td className="text-center text-warning">{team.draws}</td>
																	<td className="text-center fw-semibold">{winPercentage}%</td>
																</tr>
															);
														})}
												</tbody>
											</table>
										</div>
									) : (
										<div className="text-center py-5">
											<i className="bi bi-trophy display-1 text-muted"></i>
											<h5 className="mt-3 text-muted">No Teams Yet</h5>
											<p className="text-muted">No teams have been created for this season.</p>
										</div>
									)}
								</div>
							</div>
							{/* <div
								className={`tab-pane fade ${innerActiveTab === "Rules" ? "show active" : ""}`}
								id="tab-6"
							>
								<div>Rules TBD</div>
							</div> */}
						</div>
					</div>
				</div>

				<AddTeamModal
					show={modalVisible}
					handleClose={handleClose}
					onTeamAdded={handleTeamAdded}
				/>
				<AddLocationModal
					show={showLocationModal}
					handleClose={handleCloseLocationModal}
					onLocationAdded={handleLocationAdded}
					seasonId={seasonId}
				/>
				<AddGameModal
					show={showGameModal}         // Assume you have state for controlling Game modal visibility
					handleClose={handleCloseGameModal}  // Your function to close the modal
					onGameAdded={handleGameAdded} // Callback to update the game list
					seasonId={seasonId}           // Pass the seasonId here
				/>
			</div>
		</div>
	);
}

export default ProfileSeasonDetailPage;
